import { useRequestURL } from '#imports'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

import type { Market } from '@backmarket/http-api/src/api-models/Market'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { Bcp47Locale } from '@backmarket/http-api/src/standards/Locale'

import { getSheerIdLocaleParam } from '../../utils/sheerIdLocale'

const COUNTRIES_WITH_REDIRECTION_TO_HOME_PAGE = [
  MarketCountryCode.AU,
  MarketCountryCode.JP,
]

const getBmRedirectUrl = (market: Market) => {
  if (COUNTRIES_WITH_REDIRECTION_TO_HOME_PAGE.includes(market.countryCode))
    return ''

  if (market.countryCode === MarketCountryCode.US)
    return 'l/student-discount/e24cb331-e521-4619-a5d9-19ac82ab8b1f'

  // for all European countries, we redirect users to this student deals PLP
  return 'l/bons-plans-etudiants/255c7460-e8ec-458b-b268-da8954d3b300'
}

/**
 * Depending on the country, we need to redirect the user to a specific page after a successful student verification on the SheerID page
 */
const buildBmRedirectLandingPage = (props: {
  currentRoute: RouteLocationNormalizedLoaded
  originUrl: string
  market: Market
}) => {
  const locale = props.currentRoute.params.locale as string

  const relativeUrl = `/${locale}/${getBmRedirectUrl(props.market)}`

  return new URL(relativeUrl, props.originUrl).href
}

function buildSheerIdUrl({
  sheerIdUrl,
  market,
  bmUserId,
  currentRoute,
  originUrl,
  locale,
}: {
  sheerIdUrl: string
  market: Market
  bmUserId: string
  currentRoute: RouteLocationNormalizedLoaded
  originUrl: string
  locale: Bcp47Locale
}) {
  const url = new URL(sheerIdUrl)
  url.searchParams.append('country', market.countryCode)
  url.searchParams.append('locale', getSheerIdLocaleParam(locale))
  // note: sheerID is expecting a 'redirectURL' query param, not 'redirectUrl'
  url.searchParams.append(
    'redirectURL',
    buildBmRedirectLandingPage({ currentRoute, market, originUrl }),
  )
  url.searchParams.append('userid', bmUserId)

  return url.href
}

export function buildStudentVerificationUrl({
  currentRoute,
  market,
  bmUserId = 'USER_ID',
  sheerIdUrl,
  authenticated,
  originUrl,
  locale,
}: {
  currentRoute: RouteLocationNormalizedLoaded
  market: Market
  bmUserId?: string
  sheerIdUrl: string
  authenticated: boolean
  originUrl: string
  locale: Bcp47Locale
}) {
  const baseUrl = useRequestURL().origin

  const studentVerificationUrl = buildSheerIdUrl({
    currentRoute,
    market,
    bmUserId,
    sheerIdUrl,
    originUrl,
    locale,
  })

  if (authenticated) return studentVerificationUrl

  try {
    const redirectionUrl = new URL(`${baseUrl}/${locale}/auth/login`)
    redirectionUrl.searchParams.append('next', studentVerificationUrl)
    redirectionUrl.searchParams.append('type', 'external')
    redirectionUrl.searchParams.append('bm_journey', 'student-discount')

    return redirectionUrl.href
  } catch (err) {
    const invalidUrlError = new Error(
      `"${baseUrl}/${locale}/auth/login" is an invalid URL.`,
    )
    if (err instanceof Error) invalidUrlError.stack = err.stack
    throw invalidUrlError
  }
}
